import React, { useEffect, useState, useMemo } from 'react';
import { useShouldRemoveAds } from 'lib/subscriptionUtils';
import { GammaVideoPlayer } from 'components/Gamma/VideoPlayer';
import { OmegaVideoPlayer } from 'components/OmegaVideoPlayer';
import { GridStack } from 'components/GridStack';
import { VideoLoadingAnimation } from 'components/OmegaVideoPlayer/LoadingAnimation/Loader';
import { fetchBentoData } from 'lib/bentoUtils';
import { buildGammaStreamConfigPresetsFromEnvJson, DEFAULT_MPX_CONFIG } from 'components/OmegaVideoPlayer/gammaStreamConfigPresets';
import { useFeatureFlagContext } from 'lib/ContextTypes';

/**
 * LinearVideoPlayer - A parent component that delegates between Omega and Gamma video players
 * based on authentication state, feature flags, and stream configuration.
 *
 * @param {object} props
 */
export function LinearVideoPlayer(props) {
  const {
    stream,
    autoplay,
    mutedAutoplay,
    stickyEnabled,
    hasClickedToPlay,
    mvpdHash,
    updateIsVideoPlaying,
    onStuckChange,
    noStuckChangeInFullscreen,
    playmakerArt,
  } = props;

  const shouldRemoveAds = useShouldRemoveAds();
  const launchDarklyFlags = useFeatureFlagContext();
  const enableLiveVideoMPXIds = launchDarklyFlags?.['live-video-mpx-configuration'];
  const mxpIDs = enableLiveVideoMPXIds || DEFAULT_MPX_CONFIG;
  const mxpIDsHash = JSON.stringify(mxpIDs);

    const gammaStreamPreset = useMemo(() => {
    const presets = buildGammaStreamConfigPresetsFromEnvJson(mxpIDs);

    let preset = null;
    try {
      preset = (presets && stream) ? presets[stream] || null : null;
    } catch {
      preset = null;
    }

    return preset;
  }, [mxpIDsHash, stream]);

  // Enhanced video data fetching for Bento-based configs
  const [bentoVideoData, setBentoVideoData] = useState(null);
  const [isLoadingBentoVideoData, setIsLoadingBentoVideoData] = useState(false);

  // Fetch Bento only when the relevant primitive values change: stream, bentoUrl, or hashID.
  useEffect(() => {
    let ignore = false;
    /**
   * @returns {Promise<void>}
   */
    async function fetchAndSetBentoVideoData() {
      const bentoUrl = gammaStreamPreset?.bentoUrl;
      if (bentoUrl) {
        setIsLoadingBentoVideoData(true);
        try {
          const data = await fetchBentoData(bentoUrl);
          const hasAssets = Array.isArray(data?.data?.video?.videoAssets)
            && data.data.video.videoAssets.length > 0;
          const asset = hasAssets ? data.data.video.videoAssets[0] : null;

          if (!ignore) {
            // Use Bento data if available, otherwise fall back to preset data
            const mergedData = asset ? {
              ...gammaStreamPreset?.fallbackVideoData,
              ...data.data.video,
              headline: {
                ...gammaStreamPreset?.fallbackVideoData?.headline,
                ...data.data.video?.headline,
              },
              description: {
                ...gammaStreamPreset?.fallbackVideoData?.description,
                ...data.data.video?.description,
              },
              publisher: {
                ...gammaStreamPreset?.fallbackVideoData?.publisher,
                ...data.data.video?.publisher,
              },
            } : null;

            setBentoVideoData(mergedData);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch video data from Bento:', error);
          if (!ignore) setBentoVideoData(null);
        } finally {
          if (!ignore) setIsLoadingBentoVideoData(false);
        }
      } else {
        setBentoVideoData(null);
      }
    }
    fetchAndSetBentoVideoData();
    return () => { ignore = true; };
  }, [stream, gammaStreamPreset?.bentoUrl, mxpIDsHash]);

  // Use centralized fallback video data from preset
  const fallbackVideoData = gammaStreamPreset?.fallbackVideoData || null;

  // Use Bento data if available, otherwise fallback to preset data
  const finalVideoData = bentoVideoData || fallbackVideoData;

  // Determine if we should use Gamma player
  const shouldUseGammaPlayer = shouldRemoveAds;

  if (shouldUseGammaPlayer) {
    // Show loading state while fetching Bento data
    if (gammaStreamPreset?.bentoUrl && isLoadingBentoVideoData) {
      return (
        <GridStack data-testid="bento-loading-container" className="omega-container">
          <VideoLoadingAnimation isReady={false} />
        </GridStack>
      );
    }

    return (
      <GammaVideoPlayer
        video={finalVideoData}
        autoPlay={autoplay}
        onlyAutoplayMuted={mutedAutoplay}
        isEmbedded={false}
        useEmbeddedAds={false}
        hideVideoTitle
      />
    );
  }

  return (
    <OmegaVideoPlayer
      stream={stream}
      autoplay={autoplay}
      mutedAutoplay={mutedAutoplay}
      stickyEnabled={stickyEnabled}
      hasClickedToPlay={hasClickedToPlay}
      mvpdHash={mvpdHash}
      updateIsVideoPlaying={updateIsVideoPlaying}
      onStuckChange={onStuckChange}
      noStuckChangeInFullscreen={noStuckChangeInFullscreen}
      playmakerArt={playmakerArt}
    />
  );
}
